/**
 * shared/money.js — mọi khoản tiền trong hệ thống đi qua đây.
 *
 * Shape Money: { amount, ccy, fx, fxDate }
 *  - amount: SỐ NGUYÊN, đơn vị nhỏ nhất (đồng với VND, cent với USD)
 *  - ccy:    'VND' | 'USD'
 *  - fx:     tỷ giá quy đổi 1 đơn vị ccy = ? VND (null nếu ccy = 'VND')
 *  - fxDate: 'YYYY-MM-DD' — thời điểm áp tỷ giá
 *
 * Lý do dùng số nguyên: số thực làm tròn sai khi cộng dồn hàng trăm dòng.
 * Lý do bắt kèm fx + fxDate: một con số USD không kèm tỷ giá là con số không
 * kiểm chứng lại được sau 6 tháng.
 */

export const CURRENCIES = Object.freeze({
  VND: { minorUnits: 1, symbol: '₫', decimals: 0 },
  USD: { minorUnits: 100, symbol: '$', decimals: 2 },
});

export function money(amount, ccy = 'VND', fx = null, fxDate = null) {
  if (!CURRENCIES[ccy]) throw new Error(`Đơn vị tiền không hỗ trợ: ${ccy}`);
  if (!Number.isFinite(amount)) throw new Error('amount phải là số hữu hạn');
  if (!Number.isInteger(amount)) {
    throw new Error('amount phải là số nguyên (đơn vị nhỏ nhất). Dùng fromMajor() nếu đang có số thập phân.');
  }
  if (ccy !== 'VND' && (fx == null || !fxDate)) {
    throw new Error(`Tiền ${ccy} bắt buộc kèm fx và fxDate`);
  }
  return Object.freeze({ amount, ccy, fx, fxDate });
}

/** Từ đơn vị lớn (triệu đồng, đô la) sang Money. fromMajor(1.5, 'USD') → 150 cent */
export function fromMajor(value, ccy = 'VND', fx = null, fxDate = null) {
  const c = CURRENCIES[ccy];
  if (!c) throw new Error(`Đơn vị tiền không hỗ trợ: ${ccy}`);
  return money(Math.round(value * c.minorUnits), ccy, fx, fxDate);
}

export const ZERO_VND = money(0, 'VND');

export function isMoney(m) {
  return !!m && typeof m === 'object' && Number.isInteger(m.amount) && !!CURRENCIES[m.ccy];
}

/**
 * Quy về đồng VND. Đây là dạng duy nhất engine được phép tính toán trên đó.
 * Trả về số nguyên (đồng). null/undefined → 0.
 */
export function toVnd(m) {
  if (m == null) return 0;
  if (typeof m === 'number') {
    throw new Error('Nhận số trần thay vì Money. Mọi khoản tiền phải là shape Money.');
  }
  if (!isMoney(m)) throw new Error('Giá trị không phải Money hợp lệ');
  if (m.ccy === 'VND') return m.amount;
  const major = m.amount / CURRENCIES[m.ccy].minorUnits;
  return Math.round(major * m.fx);
}

export function addVnd(...list) {
  return list.reduce((s, m) => s + toVnd(m), 0);
}

/** So sánh: âm nếu a < b. */
export function cmp(a, b) {
  return toVnd(a) - toVnd(b);
}

export function isZero(m) {
  return toVnd(m) === 0;
}

/** Định dạng hiển thị. Không dùng trong engine, chỉ ở lớp UI. */
export function format(m, locale = 'vi-VN') {
  if (m == null) return 'chưa có';
  const c = CURRENCIES[m.ccy];
  const major = m.amount / c.minorUnits;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: m.ccy,
    maximumFractionDigits: c.decimals,
  }).format(major);
}

/** Rút gọn cho biểu đồ: 3.000.000.000 → "3 tỷ" */
export function short(m) {
  const v = toVnd(m);
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${trim(v / 1e9)} tỷ`;
  if (abs >= 1e6) return `${trim(v / 1e6)} triệu`;
  if (abs >= 1e3) return `${trim(v / 1e3)} nghìn`;
  return String(v);
}

function trim(n) {
  return String(Math.round(n * 10) / 10).replace('.', ',');
}
